import { useControllerConnectionStatus } from '../Hooks/useControllerConnectionStatus';
import React, { useState } from 'react';
import Typography from '@material-ui/core/Typography';
import { useParams } from 'react-router-dom';

import getStateManager from '../StateManager.js';
import { ahu_type_mapping } from '../Constants/constants.js';
import { LoadSettingsDialog } from '../Utils/load_settings.js';
import { SettingActions } from '../components/index.js';
import { SettingGroupGrid, ValueBox } from '../FormWidgets/SettingGroup.js';
import { ShowSetting } from '../FormWidgets/ShowSliderSetting.js';
import { EditableLabel } from '../FormWidgets/EditableLabel.js';
import { AHUControlModeSelect } from '../FormWidgets/Select.js';
import HvacWidget from '../FormWidgets/HvacWidget.js';
import Desc from '../FormWidgets/Description.js';
import { useSavedSettings, writeTempSettings } from './settingFunctions.js';

import _ from 'lodash';

const AHU_TEMP_KEY = 'equipment_ahu_unit';
const HVAC_TEMP_KEY = 'equipment_hvac';

const canRead = (rights) => rights?.read ?? rights?._read ?? false;

export default function EquipmentSettings() {
  const params = useParams();
  const stateManager = getStateManager();
  const { controller, parent } = stateManager.getControllerForTab(params);

  return (
    <ScopedEquipmentSettings
      key={`${controller.id}-${parent.id}`}
      stateManager={stateManager}
      controller={controller}
      parent={parent}
    />
  );
}

function ScopedEquipmentSettings({ stateManager, controller, parent }) {
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  const zone = String(parent.zone_id ?? 254);
  const ahuAccess = controller.access_rights?.ahu_unit ?? {};
  const hvacAccess = controller.access_rights?.hvac ?? {};

  const initialAhus = _.cloneDeep(controller.ahu_unit?.ahus ?? [])
    .filter((ahu) => String(ahu.zone) === zone)
    .sort((a, b) => a.ahu_id - b.ahu_id);
  const initialHvac = _.cloneDeep(controller.settings?.hvac ?? [])
    .map((hvac, globalIndex) => ({ ...hvac, globalIndex }))
    .filter((hvac) => String(hvac.zone) === zone);

  const [ahuValues, setAhuValues] = useState({ ahus: initialAhus });
  const [hvacValues, setHvacValues] = useState(initialHvac);

  const persistPatch = (key, stableId, field, value) => {
    const patches = _.cloneDeep(parent.temp_settings[key] ?? {});
    _.set(patches, `${stableId}.${field}`, value);
    parent.temp_settings[key] = patches;
    writeTempSettings(parent);
  };

  const ahuControls = {
    setValue: (path, value, saveTemp = true) => {
      const next = _.cloneDeep(ahuValues);
      _.set(next, path, value);
      setAhuValues(next);
      const match = path.match(/^ahus\[(\d+)]\.(.+)$/);
      if (saveTemp && match)
        persistPatch(AHU_TEMP_KEY, next.ahus[match[1]].ahu_id, match[2], value);
    },
    saveTempSettings: (path, value) => ahuControls.setValue(path, value),
    resetSetting: (path) => {
      const match = path.match(/^ahus\[(\d+)]\.(.+)$/);
      if (!match) return;
      const index = Number(match[1]);
      const id = ahuValues.ahus[index].ahu_id;
      const next = _.cloneDeep(ahuValues);
      _.set(
        next,
        path,
        _.get(
          initialAhus.find((ahu) => ahu.ahu_id === id),
          match[2]
        )
      );
      setAhuValues(next);
      _.unset(parent.temp_settings[AHU_TEMP_KEY], `${id}.${match[2]}`);
      if (_.isEmpty(parent.temp_settings[AHU_TEMP_KEY]?.[id]))
        _.unset(parent.temp_settings[AHU_TEMP_KEY], id);
      if (_.isEmpty(parent.temp_settings[AHU_TEMP_KEY])) delete parent.temp_settings[AHU_TEMP_KEY];
      writeTempSettings(parent);
    },
    resetSettings: () => {
      setAhuValues({ ahus: _.cloneDeep(initialAhus) });
      delete parent.temp_settings[AHU_TEMP_KEY];
      writeTempSettings(parent);
    },
    restoreTempSettings: () => {
      const next = { ahus: _.cloneDeep(initialAhus) };
      Object.entries(parent.temp_settings[AHU_TEMP_KEY] ?? {}).forEach(([id, patch]) => {
        const unit = next.ahus.find((ahu) => String(ahu.ahu_id) === id);
        if (unit) _.merge(unit, _.cloneDeep(patch));
      });
      setAhuValues(next);
    },
    hasChanged: (path) => {
      const match = path.match(/^ahus\[(\d+)]\.(.+)$/);
      if (!match) return false;
      const unit = ahuValues.ahus[match[1]];
      return !_.isEqual(
        _.get(unit, match[2]),
        _.get(
          initialAhus.find((ahu) => ahu.ahu_id === unit.ahu_id),
          match[2]
        )
      );
    }
  };

  const hvacControls = {
    resetSettings: () => {
      setHvacValues(_.cloneDeep(initialHvac));
      delete parent.temp_settings[HVAC_TEMP_KEY];
      writeTempSettings(parent);
    },
    restoreTempSettings: () => {
      const next = _.cloneDeep(initialHvac);
      Object.entries(parent.temp_settings[HVAC_TEMP_KEY] ?? {}).forEach(([index, patch]) => {
        const unit = next.find((hvac) => String(hvac.globalIndex) === index);
        if (unit) _.merge(unit, _.cloneDeep(patch));
      });
      setHvacValues(next);
    }
  };

  const updateHvac = (globalIndex, field, value) => {
    const next = _.cloneDeep(hvacValues);
    const unit = next.find((hvac) => hvac.globalIndex === globalIndex);
    if (!unit) return;
    unit[field] = value;
    setHvacValues(next);
    persistPatch(HVAC_TEMP_KEY, globalIndex, field, value);
  };

  const { openLoadSettingsDialog: openAhuDialog, closeLoadSettingsDialog: closeAhuDialog } =
    useSavedSettings(
      parent,
      ahuControls,
      AHU_TEMP_KEY,
      () => !_.isEmpty(parent.temp_settings[AHU_TEMP_KEY])
    );
  const { openLoadSettingsDialog: openHvacDialog, closeLoadSettingsDialog: closeHvacDialog } =
    useSavedSettings(
      parent,
      hvacControls,
      HVAC_TEMP_KEY,
      () => !_.isEmpty(parent.temp_settings[HVAC_TEMP_KEY])
    );

  const resetSettings = () => {
    if (ahuAccess.write) ahuControls.resetSettings();
    if (hvacAccess.write) hvacControls.resetSettings();
  };

  const saveSettings = () => {
    let saved = true;
    if (ahuAccess.write && controller.ahu_unit) {
      const payload = _.cloneDeep(controller.ahu_unit);
      ahuValues.ahus.forEach((edited) => {
        const index = payload.ahus.findIndex((ahu) => ahu.ahu_id === edited.ahu_id);
        if (index >= 0) payload.ahus[index] = _.cloneDeep(edited);
      });
      if (stateManager.saveSettings(controller, controller, payload, 'ahu_unit')) {
        controller.ahu_unit = _.cloneDeep(payload);
        delete parent.temp_settings[AHU_TEMP_KEY];
      } else saved = false;
    }
    if (hvacAccess.write && Array.isArray(controller.settings?.hvac)) {
      const payload = _.cloneDeep(controller.settings.hvac);
      hvacValues.forEach(({ globalIndex, ...edited }) => {
        if (payload[globalIndex]) payload[globalIndex] = _.cloneDeep(edited);
      });
      if (stateManager.saveSettings(controller, controller, payload, 'hvac')) {
        delete parent.temp_settings[HVAC_TEMP_KEY];
      } else saved = false;
    }
    writeTempSettings(parent);
    return saved;
  };

  const writeAccess = ahuAccess.write || hvacAccess.write;
  const visibleAhus =
    canRead(ahuAccess) && ahuValues.ahus.filter((unit) => unit.show_this_unit === 1);
  const visibleHvac = canRead(hvacAccess) && hvacValues.filter((unit) => unit.is_enabled === true);
  const pendingControls = [openAhuDialog && ahuControls, openHvacDialog && hvacControls].filter(
    Boolean
  );

  const closeLoadDialog = () => {
    closeAhuDialog();
    closeHvacDialog();
  };

  const saveAhuField = (index, field, value) =>
    ahuControls.setValue(`ahus[${index}].${field}`, value);

  return (
    <>
      <LoadSettingsDialog
        open={openAhuDialog || openHvacDialog}
        handleClose={closeLoadDialog}
        controls={pendingControls}
      />
      {writeAccess && <SettingActions
        title={connectionDisabledReason}
        on_default={connectionStatus !== 'good'}
        onReset={resetSettings}
        onSave={saveSettings}
      />}

      {visibleHvac &&
        visibleHvac.map((hvac) => (
          <HvacWidget
            key={hvac.globalIndex}
            id={`hvac[${hvac.globalIndex}]`}
            index={hvac.globalIndex}
            indexPerZone={hvac.index_per_zone}
            name={hvac.name}
            controlMode={hvac.control_mode}
            controllerControlMode={controller.settings.hvac[hvac.globalIndex]?.control_mode}
            operationalMode={hvac.operational_mode}
            controllerOperationalMode={
              controller.settings.hvac[hvac.globalIndex]?.operational_mode
            }
            supportedOperationalModes={hvac.supported_operational_modes}
            disabled={!hvacAccess.write}
            onChange={updateHvac}
          />
        ))}

      {visibleAhus &&
        visibleAhus.map((unit) => {
          const i = ahuValues.ahus.findIndex((ahu) => ahu.ahu_id === unit.ahu_id);
          const supported =
            unit.type_id === ahu_type_mapping.AHU_TYPE_FAN_MULTI_IO ||
            unit.type_id === ahu_type_mapping.AHU_TYPE_VALLOX_RD5;
          return (
            supported && (
              <SettingGroupGrid key={unit.ahu_id} controller={controller} id={`ahu_hl_${i + 1}`}>
                <ValueBox controller={controller} id={`ahu_unit_name_${i + 1}`}>
                  <EditableLabel
                    label="ahu_unit_name"
                    id={`ahus[${i}].name`}
                    value={unit.name ?? -1}
                    handleInput={(_, event, value) => saveAhuField(i, 'name', value)}
                    disabled={!ahuAccess.write}
                    no_label={true}
                  />
                </ValueBox>
                <></>
                {['day', 'night', 'co2'].map((period) => (
                  <ShowSetting
                    key={`fan-${period}`}
                    id={`ahus[${i}].fan_power_setpoint_${period}`}
                    show={`fan_power_setpoint_${period}`}
                    controller={controller}
                    cur_value={unit[`fan_power_setpoint_${period}`]}
                    controller_value={
                      controller.ahu_unit.ahus.find((ahu) => ahu.ahu_id === unit.ahu_id)?.[
                        `fan_power_setpoint_${period}`
                      ]
                    }
                    mod_steps={unit.fan_steps}
                    controls={ahuControls}
                    disabled={!ahuAccess.write || unit.fan_power_contr_mode === 0}
                  />
                ))}
                <ValueBox controller={controller} id={`fan_power_contr_mode_${i + 1}`}>
                  <Typography gutterBottom>
                    <Desc id="fan_power_contr_mode" />
                    <br />
                    <AHUControlModeSelect
                      controller={controller}
                      parent={parent}
                      value={unit.fan_power_contr_mode}
                      controller_value={
                        controller.ahu_unit.ahus.find((ahu) => ahu.ahu_id === unit.ahu_id)
                          ?.fan_power_contr_mode
                      }
                      onChange={(event) =>
                        saveAhuField(i, 'fan_power_contr_mode', event.target.value)
                      }
                      disabled={true || !ahuAccess.write}
                    />
                  </Typography>
                </ValueBox>
                {['day', 'night', 'co2'].map((period) => (
                  <ShowSetting
                    key={`bypass-${period}`}
                    id={`ahus[${i}].bypass_position_setpoint_${period}`}
                    show={`bypass_position_setpoint_${period}`}
                    controller={controller}
                    cur_value={unit[`bypass_position_setpoint_${period}`]}
                    controller_value={
                      controller.ahu_unit.ahus.find((ahu) => ahu.ahu_id === unit.ahu_id)?.[
                        `bypass_position_setpoint_${period}`
                      ]
                    }
                    mod_steps={unit.bypass_steps}
                    controls={ahuControls}
                    disabled={!ahuAccess.write || unit.bypass_control_mode === 0}
                  />
                ))}
                <ValueBox controller={controller} id={`bypass_control_mode_${i + 1}`}>
                  <Typography gutterBottom>
                    <Desc id="bypass_control_mode" />
                    <br />
                    <AHUControlModeSelect
                      controller={controller}
                      parent={parent}
                      value={unit.bypass_control_mode}
                      controller_value={
                        controller.ahu_unit.ahus.find((ahu) => ahu.ahu_id === unit.ahu_id)
                          ?.bypass_control_mode
                      }
                      onChange={(event) =>
                        saveAhuField(i, 'bypass_control_mode', event.target.value)
                      }
                      disabled={true || !ahuAccess.write}
                    />
                  </Typography>
                </ValueBox>
              </SettingGroupGrid>
            )
          );
        })}

      {writeAccess && <SettingActions
        title={connectionDisabledReason}
        on_default={connectionStatus !== 'good'}
        onReset={resetSettings}
        onSave={saveSettings}
      />}
    </>
  );
}
